import React, { useCallback, useEffect, useReducer } from 'react'
import moment from 'moment'
import qs from 'qs'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import { resetFlights } from 'actions/CartActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import flightSearchWidgetStateReducer, { initialState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import { FlightSearchWidgetState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { FlightsFareTypes } from 'constants/flight'
import { FlightsCreditDetails } from 'components/Flights/types'
import { isFlightsCredit } from 'selectors/flightsSelectors'

import FlightSearchWidgetDesktop from './FlightSearchWidgetDesktop'
import FlightSearchWidgetMobile from './FlightSearchWidgetMobile'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { fireInteractionEvent } from 'api/googleTagManager'
import { flightsFlightSearchClick } from 'analytics/eventDefinitions'
import * as Analytics from 'analytics/analytics'
import { searchFromHomepageEvent } from 'analytics/optimizely/events'
import ModalProvider from 'contexts/ModalProvider'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'

export enum StandaloneFlightMenu {
  departAirport = 'departAirport',
  arrivalAirport = 'arrivalAirport',
  date = 'date',
  occupants = 'occupants'
}

export interface FlightSearchWidgetSearchData extends Partial<FlightSearchWidgetState>{
  filterAirlines?: string[];
}

interface Props {
  className?: string;
  searchData?: FlightSearchWidgetSearchData;
  creditDetails?: FlightsCreditDetails;
  isFlightsCredit: boolean;
  pathname: string;
  regionCode: string;
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean;
}

function FlightSearchWidget(props: Props) {
  const dispatch = useAppDispatch()
  const { searchData, isFlightsCredit, creditDetails, className, pathname, regionCode, readOnlyAirports } = props

  const [state, stateDispatch] = useReducer(flightSearchWidgetStateReducer, {
    ...initialState,
    ...searchData,
  })

  useEffect(() => {
    if (searchData) {
      stateDispatch({
        type: FlightSearchWidgetActions.UPDATE_FLIGHT_SEARCH_WIDGET_DATA,
        payload: {
          ...state,
          ...searchData,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData])

  const {
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    occupants,
    fareType,
    fareCabin,
  } = state

  const onSearchAirport = useCallback((e?: any) => {
    e?.preventDefault()

    const transformedOccupants = countOccupantsForFlights([occupants])
    const isReturnFare = fareType.value === FlightsFareTypes.RETURN

    const queryParams = {
      originAirportCode: departureAirport?.airportCode,
      originAirportName: departureAirport?.airportName,
      destinationAirportCode: arrivalAirport?.airportCode,
      destinationAirportName: arrivalAirport?.airportName,
      departDate: moment(checkinDate).format(ISO_DATE_FORMAT),
      returnDate: isReturnFare ? moment(checkoutDate).format(ISO_DATE_FORMAT) : null,
      adults: transformedOccupants.adults,
      children: transformedOccupants.children,
      infants: transformedOccupants.infants,
      childrenAge: transformedOccupants.childrenAge.join(','),
      fareClass: fareCabin.value,
      fareType: fareType.value,
      searchId: moment().unix(),
      carrier: isFlightsCredit ? creditDetails?.carrier : undefined,
      flightsCredit: isFlightsCredit || undefined,
      filterAirlines: searchData?.filterAirlines?.join(','),
    }

    if (pathname === `/${regionCode.toLowerCase()}`) {
      Analytics.track('search-from-homepage', searchFromHomepageEvent())
    }

    fireInteractionEvent(flightsFlightSearchClick())
    dispatch(resetFlights())
    dispatch(pushWithRegion('/flights-search-results', qs.stringify(queryParams)))
  }, [
    departureAirport,
    arrivalAirport,
    occupants,
    fareType.value,
    checkinDate,
    checkoutDate,
    fareCabin.value,
    isFlightsCredit,
    creditDetails?.carrier,
    dispatch,
    pathname,
    regionCode,
    searchData?.filterAirlines,
  ])

  return (
    <FlightSearchWidgetStateProvider state={state} dispatch={stateDispatch}>
      <ModalProvider>
        <div className={className}>
          <CSSBreakpoint min="tablet">
            <FlightSearchWidgetDesktop
              onSearchAirport={onSearchAirport}
              readOnlyAirports={readOnlyAirports}
            />
          </CSSBreakpoint>
          <CSSBreakpoint max="mobile">
            <VerticalSpacer gap={12} as="form" onSubmit={onSearchAirport}>
              <FlightSearchWidgetMobile readOnlyAirports={readOnlyAirports}/>
              <TextButton kind="primary" size="large" type="submit" fit="full-width">
                Search
              </TextButton>
            </VerticalSpacer>
          </CSSBreakpoint>
        </div>
      </ModalProvider>
    </FlightSearchWidgetStateProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    creditDetails: state.flights.creditDetails,
    isFlightsCredit: isFlightsCredit(state),
    pathname: state.router.location.pathname,
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(FlightSearchWidget)
